/**
 * CTEEmbed — Embeddable CTE Analysis Panel
 * Loaded inside an iframe from the sales pipeline.
 * Receives CAD file URL via postMessage, renders full 3D analysis.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { STLViewer, ColorBar, BuildAngleGizmo } from './cte-shared';
import CTEAIPanel from './CTEAIPanel';
import {
  DEFAULT_MATERIALS,
  integrateCTE,
  rotateCTETensor,
  buildAngleToEuler,
  computeBaseRef,
  getDispMagnitude,
  interpolateCTE,
  parseSTL,
  parseOBJ,
  parseSTEP,
  decimateMesh,
  MAX_TRIANGLES,
  exportBinarySTL,
} from './cte-utils';
import './CTEEmbed.css';

const MOULD_TYPES = [
  { id: 'female', label: 'Female Mould' },
  { id: 'male', label: 'Male Mould' },
  { id: 'plug', label: 'Plug Assist' },
];

function computeBoundingBox(positions) {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  const nv = positions.length / 3;
  for (let i = 0; i < nv; i++) {
    const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2];
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
  }
  return {
    min: { x: minX, y: minY, z: minZ },
    max: { x: maxX, y: maxY, z: maxZ },
    size: { x: maxX - minX, y: maxY - minY, z: maxZ - minZ },
  };
}

export default function CTEEmbed() {
  const [materials, setMaterials] = useState(DEFAULT_MATERIALS);
  const [materialId, setMaterialId] = useState('dahltram_c250cf');
  const [t1, setT1] = useState(23);
  const [t2, setT2] = useState(120);
  const [buildAngle, setBuildAngle] = useState(0);
  const [buildAzimuth, setBuildAzimuth] = useState(0);
  const [mouldType, setMouldType] = useState('female');
  const [positions, setPositions] = useState(null);
  const [bbox, setBbox] = useState(null);
  const [triangles, setTriangles] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState(null);
  const [defScale, setDefScale] = useState(1);
  const [showOriginal, setShowOriginal] = useState(false);
  const [captured, setCaptured] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch('/api/cte/materials', { headers })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(mats => { if (Array.isArray(mats) && mats.length) setMaterials(mats); })
      .catch(() => {});
  }, []);

  const material = useMemo(
    () => materials.find(m => m.id === materialId) || materials[0],
    [materialId, materials]
  );

  const mouldMaterials = useMemo(
    () => materials.filter(m => m.category === 'mould'),
    [materials]
  );

  const orient = useMemo(
    () => buildAngleToEuler(buildAngle, buildAzimuth),
    [buildAngle, buildAzimuth]
  );

  const runAnalysis = useCallback((pos) => {
    const p = pos || positions;
    if (!p || !material) return;

    const axS = integrateCTE(material, 'ax', t1, t2) * 1e-6;
    const ayS = integrateCTE(material, 'ay', t1, t2) * 1e-6;
    const azS = integrateCTE(material, 'az', t1, t2) * 1e-6;
    const tensor = rotateCTETensor(axS, ayS, azS, orient.rx, orient.ry, orient.rz);
    const flat = tensor.flat();

    const base = computeBaseRef(p);
    const nv = p.length / 3;
    let maxDisp = 0, totalDisp = 0;
    for (let i = 0; i < nv; i++) {
      const px = p[i * 3] - base.x;
      const py = p[i * 3 + 1] - base.y;
      const pz = p[i * 3 + 2] - base.z;
      const dx = flat[0] * px + flat[1] * py + flat[2] * pz;
      const dy = flat[3] * px + flat[4] * py + flat[5] * pz;
      const dz = flat[6] * px + flat[7] * py + flat[8] * pz;
      const mag = getDispMagnitude(dx, dy, dz);
      totalDisp += mag;
      if (mag > maxDisp) maxDisp = mag;
    }

    const res = {
      maxDispMM: maxDisp,
      avgDispMM: totalDisp / nv,
      strainTensor: tensor,
      positions: p,
      base,
    };
    setResult(res);
    setCaptured(false);
    return res;
  }, [positions, material, t1, t2, orient]);

  const loadFile = useCallback(async (fileUrl, name) => {
    setLoading(true);
    setError(null);
    setResult(null);
    setPositions(null);
    setBbox(null);
    setCaptured(false);
    setFileName(name || 'CAD file');
    try {
      const resp = await fetch(fileUrl);
      if (!resp.ok) throw new Error('Download failed (' + resp.status + ')');
      const buffer = await resp.arrayBuffer();
      const ext = (name || fileUrl).split('.').pop().toLowerCase();

      let raw;
      if (ext === 'stl') raw = parseSTL(buffer);
      else if (ext === 'step' || ext === 'stp') raw = await parseSTEP(buffer);
      else if (ext === 'obj') raw = parseOBJ(new TextDecoder().decode(buffer));
      else throw new Error('Unsupported format: ' + ext);

      const decimated = decimateMesh(raw, MAX_TRIANGLES);
      const box = computeBoundingBox(raw);
      const triCount = raw.length / 9;

      setPositions(decimated.positions);
      setTriangles(triCount);
      setBbox(box);

      postToParent({
        type: 'cte-dimensions',
        dimensions: `${box.size.x.toFixed(1)}x${box.size.y.toFixed(1)}x${box.size.z.toFixed(1)}`,
        triangles: triCount,
        bbox: box,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    if (!positions) return;
    const res = runAnalysis();
    if (res && isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      postToParent({
        type: 'cte-results',
        maxDispMM: res.maxDispMM,
        avgDispMM: res.avgDispMM,
        materialId,
        materialName: material.name,
        mouldType,
        t1, t2,
        buildAngle, buildAzimuth,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialId, t1, t2, buildAngle, buildAzimuth, runAnalysis, positions]);

  useEffect(() => {
    isInitialLoadRef.current = true;
  }, [positions]);

  useEffect(() => {
    const handler = (e) => {
      if (!e.data || e.data.type !== 'cte-config') return;
      const cfg = e.data;
      if (cfg.materialId) setMaterialId(cfg.materialId);
      if (cfg.t1 !== undefined) setT1(cfg.t1);
      if (cfg.t2 !== undefined) setT2(cfg.t2);
      if (cfg.buildAngle !== undefined) setBuildAngle(cfg.buildAngle);
      if (cfg.mouldType) setMouldType(cfg.mouldType);
      if (cfg.fileUrl && cfg.fileName) {
        loadFile(cfg.fileUrl, cfg.fileName);
      } else if (cfg.fileName) {
        setFileName(cfg.fileName);
      }
    };
    window.addEventListener('message', handler);
    postToParent({ type: 'cte-ready' });
    return () => window.removeEventListener('message', handler);
  }, [loadFile]);

  const handleAngleChange = useCallback((angle, azimuth) => {
    setBuildAngle(angle);
    setBuildAzimuth(azimuth);
  }, []);

  const downloadCompensated = useCallback(() => {
    if (!result) return;
    const { positions: pos, strainTensor: tensor, base } = result;
    const flat = tensor.flat();
    const nv = pos.length / 3;
    const comp = new Float32Array(pos.length);
    for (let i = 0; i < nv; i++) {
      const px = pos[i * 3] - base.x;
      const py = pos[i * 3 + 1] - base.y;
      const pz = pos[i * 3 + 2] - base.z;
      comp[i * 3]     = pos[i * 3]     - (flat[0] * px + flat[1] * py + flat[2] * pz);
      comp[i * 3 + 1] = pos[i * 3 + 1] - (flat[3] * px + flat[4] * py + flat[5] * pz);
      comp[i * 3 + 2] = pos[i * 3 + 2] - (flat[6] * px + flat[7] * py + flat[8] * pz);
    }
    const buf = exportBinarySTL(comp);
    const blob = new Blob([buf], { type: 'application/octet-stream' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = (fileName.replace(/\.[^.]+$/, '') || 'part') + '_compensated.stl';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  }, [result, fileName]);

  const captureForReport = useCallback(() => {
    if (!result) return;
    setTimeout(() => {
      const canvas = document.querySelector('.cte-viewer-wrap canvas');
      if (canvas) {
        try {
          const image = canvas.toDataURL('image/png');
          postToParent({ type: 'cte-screenshot', image });
        } catch (_) { /* canvas tainted */ }
      }
      postToParent({
        type: 'cte-results',
        maxDispMM: result.maxDispMM,
        avgDispMM: result.avgDispMM,
        materialId,
        materialName: material.name,
        mouldType,
        t1, t2,
        buildAngle, buildAzimuth,
      });
      setCaptured(true);
    }, 300);
  }, [result, materialId, material, mouldType, t1, t2, buildAngle, buildAzimuth]);

  const handleAIFeedback = useCallback((feedback) => {
    if (feedback) {
      postToParent({ type: 'cte-ai-feedback', feedback });
    }
  }, []);

  const aiData = useMemo(() => {
    if (!result || !bbox) return null;
    const midTemp = (t1 + t2) / 2;
    const strainX = interpolateCTE(material.dataPoints, midTemp, 'ax', material.cureTemp, material.postCureScale);
    const strainY = interpolateCTE(material.dataPoints, midTemp, 'ay', material.cureTemp, material.postCureScale);
    const strainZ = interpolateCTE(material.dataPoints, midTemp, 'az', material.cureTemp, material.postCureScale);
    const aboveCure = material.cureTemp !== null && t2 > material.cureTemp;
    return {
      type: 'cad',
      materialName: material.name,
      mouldType,
      cureTemp: material.cureTemp,
      t1, t2,
      buildAngle, buildAzimuth,
      maxDispMM: result.maxDispMM.toFixed(3),
      avgDispMM: result.avgDispMM.toFixed(3),
      nTriangles: triangles,
      strainX: strainX.toFixed(2),
      strainY: strainY.toFixed(2),
      strainZ: strainZ.toFixed(2),
      aboveCure,
    };
  }, [result, bbox, material, mouldType, t1, t2, buildAngle, buildAzimuth, triangles]);

  if (loading) {
    return (
      <div className="cte-embed">
        <div className="cte-embed-loading-full">
          <div className="cte-embed-spinner-lg" />
          <div className="cte-embed-loading-text">Loading and analysing {fileName}...</div>
          <div className="cte-embed-loading-sub">Parsing geometry and running thermal analysis</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cte-embed">
        <div className="cte-embed-error">{error}</div>
      </div>
    );
  }

  if (!positions) {
    return (
      <div className="cte-embed">
        <div className="cte-embed-waiting">
          <div className="cte-embed-waiting-icon">CTE</div>
          <div>Select a part above and click <strong>Load &amp; Analyse</strong></div>
        </div>
      </div>
    );
  }

  return (
    <div className="cte-embed">
      {/* File info bar */}
      <div className="cte-embed-file-bar">
        <span className="cte-embed-file-name">{fileName}</span>
        {bbox && (
          <span className="cte-embed-file-dims">
            {bbox.size.x.toFixed(1)} x {bbox.size.y.toFixed(1)} x {bbox.size.z.toFixed(1)} mm
          </span>
        )}
        <span className="cte-embed-file-tris">{triangles.toLocaleString()} triangles</span>
      </div>

      {/* Controls bar */}
      <div className="cte-embed-controls-bar">
        <div className="cte-form-group grow">
          <label>Material</label>
          <select value={materialId} onChange={e => setMaterialId(e.target.value)}>
            {mouldMaterials.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
        <div className="cte-form-group">
          <label>Ambient</label>
          <input type="number" value={t1} onChange={e => setT1(+e.target.value)} />
        </div>
        <div className="cte-form-group">
          <label>Cure Temp</label>
          <input type="number" value={t2} onChange={e => setT2(+e.target.value)} />
        </div>
        {material?.cureTemp && (
          <div className="cte-embed-tg-badge">Tg {material.cureTemp}C</div>
        )}
      </div>

      {/* Mould type selector */}
      <div className="cte-embed-mould-type">
        <label className="cte-embed-mould-type-label">Mould Type</label>
        <div className="cte-embed-mould-type-btns">
          {MOULD_TYPES.map(mt => (
            <button
              key={mt.id}
              className={`cte-embed-mould-btn ${mouldType === mt.id ? 'active' : ''}`}
              onClick={() => setMouldType(mt.id)}
            >
              {mt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      {result && (
        <div className="cte-embed-stats-row">
          <div className="cte-embed-stat">
            <span className="cte-embed-stat-label">Max Distortion</span>
            <span className="cte-embed-stat-value">{result.maxDispMM.toFixed(3)} mm</span>
          </div>
          <div className="cte-embed-stat">
            <span className="cte-embed-stat-label">Avg Distortion</span>
            <span className="cte-embed-stat-value">{result.avgDispMM.toFixed(3)} mm</span>
          </div>
          <div className="cte-embed-stat">
            <span className="cte-embed-stat-label">Delta T</span>
            <span className="cte-embed-stat-value">{Math.abs(t2 - t1)}C</span>
          </div>
          <div className="cte-embed-stat">
            <span className="cte-embed-stat-label">Build Angle</span>
            <span className="cte-embed-stat-value">{buildAngle} deg</span>
          </div>
        </div>
      )}

      {/* Two-column: Gizmo + 3D Viewer */}
      {result && positions && (
        <div className="cte-embed-two-col">
          <div className="cte-embed-col-gizmo">
            <BuildAngleGizmo
              buildAngle={buildAngle}
              buildAzimuth={buildAzimuth}
              onAngleChange={handleAngleChange}
              size={bbox ? bbox.size : null}
            />
          </div>
          <div className="cte-embed-col-viewer">
            <div className="cte-embed-viewer-toolbar">
              <label className="cte-embed-checkbox">
                <input type="checkbox" checked={showOriginal} onChange={e => setShowOriginal(e.target.checked)} />
                Wireframe overlay
              </label>
              <div className="cte-embed-scale">
                <label>Scale</label>
                <input type="range" min="1" max="50" value={defScale} onChange={e => setDefScale(+e.target.value)} />
                <span>{defScale === 1 ? 'true scale' : `${defScale}× shown larger`}</span>
              </div>
            </div>
            <STLViewer
              positions={positions}
              strainTensor={result.strainTensor}
              maxDisp={result.maxDispMM}
              showOriginal={showOriginal}
              orient={orient}
              defScale={defScale}
            />
            <ColorBar maxVal={result.maxDispMM} unit="mm" />
          </div>
        </div>
      )}

      {/* Actions */}
      {result && (
        <div className="cte-embed-actions">
          <button className="cte-btn cte-btn-primary" onClick={captureForReport}>
            {captured ? 'Captured' : 'Capture for Report'}
          </button>
          <button className="cte-btn cte-btn-ghost" onClick={downloadCompensated}>
            Download Compensated STL
          </button>
        </div>
      )}

      {/* AI Feedback */}
      {result && (
        <CTEAIPanel
          analysisData={aiData}
          label="AI Material & Distortion Insight"
          endpoint="/api/cte/ai/analyse"
          autoRun={false}
          onFeedback={handleAIFeedback}
        />
      )}
    </div>
  );
}

function postToParent(data) {
  if (window.parent !== window) {
    window.parent.postMessage(data, '*');
  }
}
